import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { usePageTitle } from './hooks/usePageTitle';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { TopoBackground } from './components/TopoBackground'; // adjust path to match your project

function Home() {
  usePageTitle('Home');
  const ADMIN_CACHE_KEY = 'ts_isAdmin';
  const USERNAME_CACHE_KEY = 'ts_username';
  const db = getFirestore();

  const seedRef = useRef(Math.floor(Math.random() * 99999));

  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try { return localStorage.getItem(ADMIN_CACHE_KEY) === 'true'; } catch { return false; }
  });

  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [cachedName, setCachedName] = useState<string | null>(() => {
    try { return localStorage.getItem(USERNAME_CACHE_KEY); } catch { return null; }
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setInitialized(true);

      if (u) {
        const name = u.displayName ?? u.email ?? null;
        if (name) {
          setCachedName(name);
          try { localStorage.setItem(USERNAME_CACHE_KEY, name); } catch {}
        }

        try {
          const userDoc = await getDoc(doc(db, 'users', u.uid));
          const groups: string[] = userDoc.data()?.groups ?? [];
          const adminStatus =
            groups.includes('groupAdministrator') || groups.includes('groupsSuperUser');
          setIsAdmin(adminStatus);
          try { localStorage.setItem(ADMIN_CACHE_KEY, String(adminStatus)); } catch {}
        } catch (e) {
          console.error('Failed to check admin status:', e);
        }
      } else {
        setCachedName(null);
        setIsAdmin(false);
        try {
          localStorage.removeItem(USERNAME_CACHE_KEY);
          localStorage.removeItem(ADMIN_CACHE_KEY);
        } catch {}
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      try {
        setIsAdmin(localStorage.getItem(ADMIN_CACHE_KEY) === 'true');
        setCachedName(localStorage.getItem(USERNAME_CACHE_KEY));
      } catch {}
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const showSignedIn = Boolean(user) || (!initialized && cachedName != null);
  const effectiveName = user?.displayName ?? user?.email ?? cachedName;

  return (
    <div
      id="ts-home-hero"
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      style={{ background: '#212224' }}
    >
      {/* animated topographic background */}
      <TopoBackground seed={seedRef.current} />

      {/* vignette — darkens edges, keeps centre clear */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(33,34,36,0.75) 100%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* content */}
      <div
        className="relative w-full max-w-3xl p-8 sm:p-12 text-center space-y-5 text-light-text-alt"
        style={{ zIndex: 2 }}
      >
        <h1 className="text-3xl sm:text-4xl font-bold">
          Welcome to <span className="text-light-main">TerraSciences</span>
        </h1>

        {showSignedIn ? (
          <>
            <p className="text-lg">
              Hi <span className="font-semibold">{effectiveName}</span>, you can access your
              downloads and account status below.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a
                href="/account/downloads"
                className="inline-flex items-center justify-center px-6 py-3 text-md font-semibold rounded-md bg-light-main text-light-text-alt hover:bg-light-main-hover focus:outline-none focus:ring-2 focus:ring-light-main/40"
              >
                Go to downloads
              </a>

              <a
                href="/account/profile"
                className="inline-flex items-center justify-center px-6 py-3 text-md font-semibold rounded-md border border-light-border hover:border-light-text-alt bg-transparent focus:outline-none focus:ring-2 focus:ring-light-main/40"
              >
                View account status
              </a>

              {isAdmin && (
                <a
                  href="/admin"
                  className="inline-flex items-center justify-center px-6 py-3 text-md font-semibold rounded-md bg-light-main text-light-text-alt hover:bg-light-main-hover duration-200"
                >
                  Admin
                </a>
              )}
            </div>
          </>
        ) : initialized ? (
          <>
            <p className="text-lg">
              Sign in to access your downloads, updates, and account details.
            </p>
            <a
              href="/login"
              className="inline-flex items-center justify-center px-8 py-3 mt-4 text-md border border-light-border font-semibold rounded-md text-light-text-alt focus:outline-none focus:ring-2 focus:ring-light-main/40"
            >
              Sign in
            </a>
          </>
        ) : (
          <p className="text-lg">Loading…</p>
        )}
      </div>
    </div>
  );
}

export default Home;
